// Tracks which databases / apps / automation-workflows the
// user opened most recently. Used by the Global Search Modal's Recently-
// opened section to surface the user's top jump-back targets without a
// per-user backend table.
//
// Pattern mirrors `lastVisitedTableStore.js`: localStorage-only, per-device
// is intentional, never throws (storage disabled / quota exceeded /
// malformed JSON are all silent fallbacks).

const STORAGE_KEY = 'baserow.recentlyOpened'
const CAP = 20
// Per-target write throttle. A user clicking around the same app
// shouldn't write a fresh timestamp on every page change.
const THROTTLE_MS = 5000

const VALID_TYPES = new Set(['database', 'app', 'automation'])

function getStorage() {
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) {
      return null
    }
    return localStorage
  } catch (e) {
    return null
  }
}

function isValidEntry(entry) {
  return (
    entry &&
    typeof entry === 'object' &&
    VALID_TYPES.has(entry.type) &&
    Number.isFinite(entry.id) &&
    Number.isFinite(entry.last_opened_at)
  )
}

function readRaw(storage) {
  const raw = storage.getItem(STORAGE_KEY)
  if (raw === null || raw === '') return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidEntry)
  } catch (e) {
    return []
  }
}

function writeRaw(storage, entries) {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch (e) {
    // Storage disabled / quota exceeded — silently drop.
  }
}

function sameTarget(a, b) {
  return a.type === b.type && a.id === b.id
}

/**
 * Returns the recently-opened entries sorted by last_opened_at descending.
 * Malformed entries and unknown types are filtered out, but the underlying
 * storage is not rewritten — callers using remove() drive cleanup.
 */
export function read() {
  const storage = getStorage()
  if (!storage) return []
  const entries = readRaw(storage)
  return entries.slice().sort((a, b) => b.last_opened_at - a.last_opened_at)
}

/**
 * Returns the last_opened_at timestamp for one specific target, or null if
 * the target hasn't been opened recently / was evicted from the cache.
 */
export function readByTarget(target) {
  if (!target || !VALID_TYPES.has(target.type)) return null
  const id = Number.parseInt(target.id, 10)
  if (!Number.isFinite(id)) return null
  const storage = getStorage()
  if (!storage) return null
  const entries = readRaw(storage)
  const entry = entries.find((e) => e.type === target.type && e.id === id)
  return entry ? entry.last_opened_at : null
}

/**
 * Records a fresh open for the given target. The write is skipped when the
 * same target was bumped less than 5 seconds ago (THROTTLE_MS), which keeps
 * the list stable while the user navigates within a single application.
 */
export function write(target) {
  const storage = getStorage()
  if (!storage) return
  if (!target || !VALID_TYPES.has(target.type)) return
  const id = Number.parseInt(target.id, 10)
  if (!Number.isFinite(id)) return

  const now = Date.now()
  const entries = readRaw(storage)
  const existingIndex = entries.findIndex((e) =>
    sameTarget(e, { type: target.type, id })
  )

  if (existingIndex !== -1) {
    const existing = entries[existingIndex]
    if (now - existing.last_opened_at < THROTTLE_MS) {
      return
    }
    existing.last_opened_at = now
  } else {
    entries.push({ type: target.type, id, last_opened_at: now })
  }

  // Cap by dropping the oldest entries (smallest last_opened_at).
  if (entries.length > CAP) {
    entries.sort((a, b) => b.last_opened_at - a.last_opened_at)
    entries.length = CAP
  }

  writeRaw(storage, entries)
}

/**
 * Records applications that have just been put in front of the user without
 * them navigating anywhere: an import, a duplicate, an installed template.
 *
 * The lists this store orders answer "when did you last have this in front of
 * you", and bringing a base into the workspace is exactly that — it is the
 * thing the user came here to do and the one they are about to open. Without
 * this, a freshly imported base sorts below every base that was ever opened,
 * which is the last place anybody looks for it.
 *
 * Entries whose type the store does not track are skipped, so a caller can
 * hand over whatever the job handed it.
 */
export function writeArrived(applications) {
  for (const application of applications || []) {
    write({ type: application?.type, id: application?.id })
  }
}

/**
 * Removes a specific entry from storage. The Recents UI calls this lazily
 * when an entry can't be resolved against the application store any more.
 */
export function remove(target) {
  const storage = getStorage()
  if (!storage || !target) return
  const id = Number.parseInt(target.id, 10)
  if (!Number.isFinite(id)) return
  const entries = readRaw(storage).filter(
    (e) => !sameTarget(e, { type: target.type, id })
  )
  writeRaw(storage, entries)
}

/**
 * Wipes the entire list. Reserved for a future logout-hook; not used by
 * the default tracker.
 */
export function clear() {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (e) {
    // graceful fallback
  }
}

export default { read, write, writeArrived, remove, clear }
